import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Compass, TriangleAlert } from "lucide-react";
import Link from "@/lib/router/link";
import { Button } from "@/components/ui/button";

/**
 * What a viewer sees for an unmatched URL or an unhandled render error.
 *
 * Attached as the root route's `errorElement` rather than as a `path: "*"`
 * catch-all: React Router raises a 404 ErrorResponse for an unmatched URL and
 * the nearest errorElement receives it, so this covers both cases at once —
 * and, unlike a catch-all, it adds no entry to the route table that
 * `test/routes.test.ts` would then fail to find a page for.
 *
 * Without it React Router renders its own developer page ("Hey developer 👋"),
 * which is what a user hit after this release removed the standalone AI-agent
 * routes.
 */
/**
 * A page's code could not be downloaded (2.713 made every page a separate
 * download). The usual causes are a redeploy that replaced this tab's chunks
 * and a dropped connection; neither is a console bug, and the cure is a
 * reload. The wordings are the browsers' own for a failed dynamic import.
 */
export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error &&
    /dynamically imported module|Importing a module script failed|Failed to load module script|Unable to preload CSS/i.test(error.message);
}

export function RouteError() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const chunk = !notFound && isChunkLoadError(error);
  // 3.106: the technical text is for support, so it sits under a Details
  // disclosure (the house pattern, app/(account)/issues/[id]/page.tsx), never
  // on the reading line. A thrown non-404 response has no message, only a status.
  const technical = notFound ? undefined
    : error instanceof Error ? error.message
    : isRouteErrorResponse(error) ? `${error.status} ${error.statusText}`.trim()
    : undefined;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md text-center">
        <span className="mx-auto mb-5 flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
          {notFound ? <Compass className="size-5" /> : <TriangleAlert className="size-5" />}
        </span>

        <h1 className="font-display text-2xl font-semibold tracking-tight">
          {notFound ? "That page doesn't exist" : chunk ? "This page could not be downloaded" : "Something went wrong"}
        </h1>

        {/* 3.106: the 404 says only that the page doesn't exist; the sentence
            about AI testing's old address was news for one release, not an
            answer to a wrong link. Each line is one or two plain sentences. */}
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {notFound
            ? "Check the address, or go back to the overview."
            : chunk
              ? "A newer version of the console may have been deployed since this tab was opened, or the connection dropped. Reload to continue."
              : "The console hit an unexpected error. Reloading usually fixes it; if it keeps happening, share the details with support."}
        </p>

        {technical && (
          <details className="mt-4 text-xs text-muted-foreground">
            <summary className="cursor-pointer">Details</summary>
            <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded-lg border bg-muted/40 px-3.5 py-3 text-left font-mono text-[11px] leading-relaxed">
              {technical}
            </pre>
          </details>
        )}

        <div className="mt-7 flex items-center justify-center gap-2.5">
          {/* 3.106: a missing page has nothing to reload; the way back is the overview. */}
          {notFound ? (
            <Button asChild>
              <Link href="/overview">Go to overview</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="outline">
                <Link href="/overview">Go to overview</Link>
              </Button>
              <Button onClick={() => window.location.reload()}>Reload</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RouteError;
